import React, { useRef, useState, useEffect } from 'react';

const LetterCanvas = ({ image, strokes, onComplete, onResetRef }) => {
    const canvasRef = useRef(null);
    const [drawing, setDrawing] = useState(false);
    const currentStroke = useRef([]);
    const [message, setMessage] = useState('');
    const [completedStrokes, setCompletedStrokes] = useState(new Set());
    const [showHint, setShowHint] = useState(true);
    const [hasStartedDrawing, setHasStartedDrawing] = useState(false);
    const [allDrawnStrokes, setAllDrawnStrokes] = useState([]);
    const [isChecking, setIsChecking] = useState(false);
    const checkTimeoutRef = useRef(null);

    const resetCanvas = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        setCompletedStrokes(new Set());
        setMessage('');
        setShowHint(true);
        setHasStartedDrawing(false);
        setAllDrawnStrokes([]);
        setIsChecking(false);
        if (checkTimeoutRef.current) {
            clearTimeout(checkTimeoutRef.current);
        }
    };

    // Expose reset function to parent
    useEffect(() => {
        if (onResetRef) {
            onResetRef(resetCanvas);
        }
    }, [onResetRef]);

    // Show animated hint when component mounts or resets
    useEffect(() => {
        if (showHint && canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            let isCancelled = false;

            const animateAllStrokes = () => {
                let currentStrokeIndex = 0;

                const animateNextStroke = () => {
                    if (isCancelled || !showHint || hasStartedDrawing) return;

                    if (currentStrokeIndex >= strokes.length) {
                        // All strokes drawn, wait then loop
                        setTimeout(() => {
                            if (!isCancelled && showHint && !hasStartedDrawing) {
                                ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                                animateAllStrokes();
                            }
                        }, 1500);
                        return;
                    }

                    const stroke = strokes[currentStrokeIndex];
                    const interpolatedPoints = generateInterpolatedStroke(stroke);
                    let pointIndex = 0;

                    const drawStrokeAnimation = () => {
                        if (isCancelled || !showHint || hasStartedDrawing) return;

                        if (pointIndex < interpolatedPoints.length - 1) {
                            ctx.beginPath();
                            ctx.moveTo(interpolatedPoints[pointIndex].x, interpolatedPoints[pointIndex].y);
                            ctx.lineTo(interpolatedPoints[pointIndex + 1].x, interpolatedPoints[pointIndex + 1].y);
                            ctx.strokeStyle = 'rgba(0, 150, 255, 0.6)';
                            ctx.lineWidth = 5;
                            ctx.lineCap = 'round';
                            ctx.lineJoin = 'round';
                            ctx.stroke();
                            pointIndex++;
                            requestAnimationFrame(drawStrokeAnimation);
                        } else {
                            // This stroke is complete, move to next stroke
                            currentStrokeIndex++;
                            setTimeout(() => {
                                if (!isCancelled && showHint && !hasStartedDrawing) {
                                    animateNextStroke();
                                }
                            }, 400);
                        }
                    };

                    drawStrokeAnimation();
                };

                animateNextStroke();
            };

            animateAllStrokes();

            return () => {
                isCancelled = true;
            };
        }
    }, [showHint, strokes, hasStartedDrawing]);

    const generateInterpolatedStroke = ({ start, end }) => {
        const points = [];
        const steps = 20;
        for (let i = 0; i <= steps; i++) {
            points.push({
                x: start.x + (end.x - start.x) * (i / steps),
                y: start.y + (end.y - start.y) * (i / steps),
            });
        }
        return points;
    };

    const getCanvasPos = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const clientX = e.clientX || e.touches?.[0]?.clientX;
        const clientY = e.clientY || e.touches?.[0]?.clientY;
        // The board may be scaled down to fit the screen (GameFitBox), so
        // convert from on-screen pixels back to the canvas's own pixels.
        const scale = rect.width / (canvasRef.current.offsetWidth || rect.width) || 1;
        return { x: (clientX - rect.left) / scale, y: (clientY - rect.top) / scale };
    };

    const startDrawing = (e) => {
        e.preventDefault();
        
        // Hide hint when user starts drawing
        if (showHint) {
            setShowHint(false);
            setHasStartedDrawing(true);
            const ctx = canvasRef.current.getContext('2d');
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
        
        setDrawing(true);
        currentStroke.current = [getCanvasPos(e)];
    };

    const draw = (e) => {
        if (!drawing) return;
        const ctx = canvasRef.current.getContext('2d');
        const pos = getCanvasPos(e);
        const last = currentStroke.current[currentStroke.current.length - 1];

        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 5;
        ctx.stroke();

        currentStroke.current.push(pos);
    };

    const endDrawing = () => {
        if (!drawing) return;
        setDrawing(false);
        const stroke = [...currentStroke.current];
        
        // Store all drawn strokes
        setAllDrawnStrokes(prev => [...prev, stroke]);
        
        // No auto-check - user must click "Check My Drawing" button
        currentStroke.current = [];
    };

    const checkCompleteDrawing = () => {
        if (isChecking || allDrawnStrokes.length === 0) return;
        
        setIsChecking(true);
        setMessage('Checking your drawing...');
        
        // Flatten all drawn points for analysis
        const allDrawnPoints = allDrawnStrokes.flat();
        
        // Step 1: Check bounding box similarity
        const boundingBoxScore = calculateBoundingBoxSimilarity(allDrawnPoints, strokes);
        if (boundingBoxScore < 0.65) {
            // Shape is completely wrong
            setMessage('❌ Try again! Draw the letter shown above.');
            const utterance = new SpeechSynthesisUtterance('Try again');
            utterance.rate = 0.9;
            utterance.volume = 1;
            speechSynthesis.speak(utterance);
            
            const wrongSound = new Audio('/sounds/wrong.mp3');
            wrongSound.volume = 1;
            wrongSound.play().catch(err => console.error('Error playing wrong sound:', err));
            
            setTimeout(() => {
                const ctx = canvasRef.current.getContext('2d');
                ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                setAllDrawnStrokes([]);
                setMessage('');
                setIsChecking(false);
                setShowHint(true);
                setHasStartedDrawing(false);
            }, 2000);
            setIsChecking(false);
            return;
        }
        
        // Step 2: Validate drawing against expected strokes
        const matchedStrokes = new Set();
        
        for (let i = 0; i < strokes.length; i++) {
            const expectedStroke = strokes[i];
            let bestMatch = false;
            
            // Check each drawn stroke against this expected stroke
            for (const drawnStroke of allDrawnStrokes) {
                if (drawnStroke.length < 2) continue;
                
                // Check if drawn stroke covers the expected stroke
                if (strokeMatchesExpected(drawnStroke, expectedStroke, strokes.length)) {
                    bestMatch = true;
                    break;
                }
            }
            
            if (bestMatch) {
                matchedStrokes.add(i);
            }
        }
        
        // Calculate match percentage
        const matchPercentage = (matchedStrokes.size / strokes.length) * 100;
        
        // Dynamic validation based on letter complexity
        let requiredPercentage = 75;
        let minStrokeCount = 2;
        
        if (strokes.length >= 12) {
            // Very complex: G (12), O (12), Q (13), S (13)
            requiredPercentage = 45;
            minStrokeCount = 4;
        } else if (strokes.length >= 11) {
            // Very complex: B (11)
            requiredPercentage = 50;
            minStrokeCount = 4;
        } else if (strokes.length >= 9) {
            // Complex: C (9)
            requiredPercentage = 55;
            minStrokeCount = 4;
        } else if (strokes.length >= 7) {
            // Moderately complex: D (8), R (8), P (7), U (7)
            requiredPercentage = 60;
            minStrokeCount = 3;
        } else if (strokes.length >= 4) {
            // Medium: E (4), M (4), N (4), W (4)
            requiredPercentage = 70;
            minStrokeCount = 2;
        } else if (strokes.length === 2) {
            // Very simple: L (2), T (2), V (2), X (2)
            requiredPercentage = 75;
            minStrokeCount = 2;
        }
        
        // Check if drawing meets requirements
        const isValid = matchPercentage >= requiredPercentage && matchedStrokes.size >= minStrokeCount;
        
        if (isValid) {
            // Success - drawing is correct
            setMessage('🎉 Correct! Perfect letter!');
            setCompletedStrokes(matchedStrokes);
            
            // Speak "Excellent"
            const utterance = new SpeechSynthesisUtterance('Excellent');
            utterance.rate = 0.9;
            utterance.volume = 1;
            speechSynthesis.speak(utterance);
            
            // Play success sound
            const correctSound = new Audio('/sounds/correct.mp3');
            correctSound.volume = 1;
            correctSound.play().catch(err => console.error('Error playing correct sound:', err));
            
            // Overlay correct strokes and complete
            setTimeout(() => {
                overlayCorrectStrokes();
                setTimeout(() => {
                    onComplete?.();
                }, 1500);
            }, 800);
        } else {
            // Failed validation
            setMessage('❌ Try again! Draw the letter shown above.');
            
            // Speak "try again"
            const utterance = new SpeechSynthesisUtterance('Try again');
            utterance.rate = 0.9;
            utterance.volume = 1;
            speechSynthesis.speak(utterance);
            
            // Play wrong sound
            const wrongSound = new Audio('/sounds/wrong.mp3');
            wrongSound.volume = 1;
            wrongSound.play().catch(err => console.error('Error playing wrong sound:', err));
            
            // Auto-clear after 2 seconds
            setTimeout(() => {
                const ctx = canvasRef.current.getContext('2d');
                ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                setAllDrawnStrokes([]);
                setMessage('');
                setIsChecking(false);
                setShowHint(true);
                setHasStartedDrawing(false);
            }, 2000);
        }
        
        setIsChecking(false);
    };

    const calculateBoundingBoxSimilarity = (drawnPoints, expectedStrokes) => {
        if (drawnPoints.length === 0) return 0;
        
        // Get drawn bounding box
        const drawnBounds = {
            minX: Math.min(...drawnPoints.map(p => p.x)),
            maxX: Math.max(...drawnPoints.map(p => p.x)),
            minY: Math.min(...drawnPoints.map(p => p.y)),
            maxY: Math.max(...drawnPoints.map(p => p.y))
        };
        
        // Get expected bounding box
        const expectedPoints = [];
        expectedStrokes.forEach(stroke => {
            expectedPoints.push(stroke.start, stroke.end);
        });
        
        const expectedBounds = {
            minX: Math.min(...expectedPoints.map(p => p.x)),
            maxX: Math.max(...expectedPoints.map(p => p.x)),
            minY: Math.min(...expectedPoints.map(p => p.y)),
            maxY: Math.max(...expectedPoints.map(p => p.y))
        };
        
        // Calculate width and height ratios
        const drawnWidth = drawnBounds.maxX - drawnBounds.minX;
        const drawnHeight = drawnBounds.maxY - drawnBounds.minY;
        const expectedWidth = expectedBounds.maxX - expectedBounds.minX;
        const expectedHeight = expectedBounds.maxY - expectedBounds.minY;
        
        if (expectedWidth === 0 || expectedHeight === 0) return 0;
        
        const widthRatio = Math.min(drawnWidth, expectedWidth) / Math.max(drawnWidth, expectedWidth);
        const heightRatio = Math.min(drawnHeight, expectedHeight) / Math.max(drawnHeight, expectedHeight);
        
        // Calculate center offset
        const drawnCenterX = (drawnBounds.minX + drawnBounds.maxX) / 2;
        const drawnCenterY = (drawnBounds.minY + drawnBounds.maxY) / 2;
        const expectedCenterX = (expectedBounds.minX + expectedBounds.maxX) / 2;
        const expectedCenterY = (expectedBounds.minY + expectedBounds.maxY) / 2;
        
        const centerOffset = Math.hypot(drawnCenterX - expectedCenterX, drawnCenterY - expectedCenterY);
        const maxDimension = Math.max(expectedWidth, expectedHeight);
        const centerScore = Math.max(0, 1 - (centerOffset / maxDimension));
        
        // Combined score
        return (widthRatio * 0.35 + heightRatio * 0.35 + centerScore * 0.3);
    };

    const strokeMatchesExpected = (drawnStroke, expectedStroke, totalStrokes) => {
        // Dynamic tolerance based on letter complexity
        let tolerance = 50;
        if (totalStrokes >= 12) {
            tolerance = 68;
        } else if (totalStrokes >= 11) {
            tolerance = 65;
        } else if (totalStrokes >= 9) {
            tolerance = 62;
        } else if (totalStrokes >= 7) {
            tolerance = 56;
        } else if (totalStrokes >= 4) {
            tolerance = 52;
        }
        
        // Count points near start, end, and path
        let nearStartCount = 0;
        let nearEndCount = 0;
        let nearPathCount = 0;
        
        for (const point of drawnStroke) {
            // Distance to start point
            const distToStart = Math.hypot(
                point.x - expectedStroke.start.x, 
                point.y - expectedStroke.start.y
            );
            if (distToStart < tolerance) nearStartCount++;
            
            // Distance to end point
            const distToEnd = Math.hypot(
                point.x - expectedStroke.end.x, 
                point.y - expectedStroke.end.y
            );
            if (distToEnd < tolerance) nearEndCount++;
            
            // Distance to line path
            const distToLine = pointToLineDistance(point, expectedStroke.start, expectedStroke.end);
            if (distToLine < tolerance) nearPathCount++;
        }
        
        // Stricter requirements
        const hasStart = nearStartCount >= 3; // At least 3 points near start
        const hasEnd = nearEndCount >= 3; // At least 3 points near end
        const hasPath = nearPathCount > drawnStroke.length * 0.40; // 40% of points need to be on path
        
        return hasStart && hasEnd && hasPath;
    };

    const pointToLineDistance = (point, lineStart, lineEnd) => {
        const A = point.x - lineStart.x;
        const B = point.y - lineStart.y;
        const C = lineEnd.x - lineStart.x;
        const D = lineEnd.y - lineStart.y;
        
        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        
        if (lenSq === 0) return Math.hypot(A, B);
        
        let param = dot / lenSq;
        param = Math.max(0, Math.min(1, param));
        
        const xx = lineStart.x + param * C;
        const yy = lineStart.y + param * D;
        
        return Math.hypot(point.x - xx, point.y - yy);
    };


    const overlayCorrectStrokes = () => {
        // Draw correct strokes in green over the user's drawing (don't clear canvas)
        const ctx = canvasRef.current.getContext('2d');
        ctx.lineWidth = 6;
        ctx.strokeStyle = 'rgba(39, 174, 96, 0.7)'; // Semi-transparent green
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Draw all correct strokes
        strokes.forEach(stroke => {
            const points = generateInterpolatedStroke(stroke);
            ctx.beginPath();
            ctx.moveTo(points[0].x, points[0].y);
            for (let i = 1; i < points.length; i++) {
                ctx.lineTo(points[i].x, points[i].y);
            }
            ctx.stroke();
        });
    };



    return (
        <>
            <div style={{ position: 'relative', width: '100%', maxWidth: 300, height: 'auto', aspectRatio: '1/1' }}>
                <img src={image} alt="letter" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                <canvas
                    ref={canvasRef}
                    width={300}
                    height={300}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', cursor: 'crosshair' }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={endDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={endDrawing}
                />
            </div>
            {message && (
                <div style={{ 
                    marginTop: 10, 
                    fontWeight: 'bold', 
                    fontSize: '16px',
                    color: message.includes('Perfect') || message.includes('Correct') ? '#27AE60' : 
                           message.includes('Keep') || message.includes('Checking') ? '#3498DB' : '#E74C3C' 
                }}>
                    {message}
                </div>
            )}
            {hasStartedDrawing && allDrawnStrokes.length > 0 && !message.includes('Perfect') && (
                <button
                    onClick={() => {
                        if (checkTimeoutRef.current) {
                            clearTimeout(checkTimeoutRef.current);
                        }
                        checkCompleteDrawing();
                    }}
                    style={{
                        marginTop: '12px',
                        padding: '10px 24px',
                        fontSize: '16px',
                        fontWeight: '600',
                        borderRadius: '10px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                        color: '#fff',
                        cursor: 'pointer',
                        boxShadow: '0 4px 10px rgba(52, 152, 219, 0.3)',
                        transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                    ✓ Check My Drawing
                </button>
            )}
        </>
    );
};

export default LetterCanvas;
